import React, { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Paper,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import WarningRoundedIcon from "@material-ui/icons/ReportProblemOutlined";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import PromptModal from "../../components/PromptModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";
import { financeiroStyles, toneColor } from "../Financeiro/financeiroShared";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";
import { modelLabel, isVoice, voiceLabel } from "../../components/PromptModal/promptOptions";

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
// Eventos do socket não trazem onde o prompt está em uso: mantém o que já
// estava na tela.
const merge = (prev, next) => ({
  ...next,
  usedInConnections: next.usedInConnections || (prev && prev.usedInConnections) || [],
  usedInQueues: next.usedInQueues || (prev && prev.usedInQueues) || [],
});

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD": {
      const next = [...state];
      action.payload.forEach((item) => {
        const idx = next.findIndex((i) => i.id === item.id);
        if (idx !== -1) next[idx] = merge(next[idx], item);
        else next.push(merge(null, item));
      });
      return next;
    }
    case "UPSERT": {
      const idx = state.findIndex((i) => i.id === action.payload.id);
      if (idx === -1) return [merge(null, action.payload), ...state];
      const next = [...state];
      next[idx] = merge(state[idx], action.payload);
      return next;
    }
    case "DELETE":
      return state.filter((i) => i.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const usageCount = (item) =>
  (item.usedInConnections || []).length + (item.usedInQueues || []).length;

const FILTERS = [
  ["all", "Todos"],
  ["inuse", "Em uso"],
  ["unused", "Sem uso"],
  ["scheduling", "Com agenda"],
];

const matchesFilter = (item, filter) => {
  if (filter === "inuse") return usageCount(item) > 0;
  if (filter === "unused") return usageCount(item) === 0;
  if (filter === "scheduling") return Boolean(item.allowScheduling);
  return true;
};

const fmtNumber = (n) => Number(n || 0).toLocaleString("pt-BR");

const usageText = (item) => {
  const c = (item.usedInConnections || []).length;
  const q = (item.usedInQueues || []).length;
  const parts = [];
  if (c) parts.push(`${c} ${c === 1 ? "conexão" : "conexões"}`);
  if (q) parts.push(`${q} ${q === 1 ? "setor" : "setores"}`);
  return parts.join(" · ");
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  ...financeiroStyles(theme),
  header: { ...financeiroStyles(theme).header, flexShrink: 0 },
  creditNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    margin: theme.spacing(1.5, 2.5, 0),
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.45,
    color: theme.palette.text.primary,
    backgroundColor: alpha(toneColor(theme, "amber"), theme.palette.type === "dark" ? 0.16 : 0.1),
    "& a": { color: theme.palette.primary.main, fontWeight: 600 },
    [theme.breakpoints.down("xs")]: { margin: theme.spacing(1.5, 1.5, 0) },
  },
  creditIcon: { fontSize: 18, color: toneColor(theme, "amber"), marginTop: 1, flexShrink: 0 },
  grid: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1.5, 2.5, 2),
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    alignContent: "start",
    gap: theme.spacing(1.5),
    ...sidebarScrollbar(theme),
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(1.5), gridTemplateColumns: "1fr" },
  },
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.75),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    minWidth: 0,
    backgroundColor: theme.palette.background.paper,
  },
  cardHead: { display: "flex", alignItems: "center", gap: theme.spacing(1.25), minWidth: 0 },
  badge: {
    width: 38,
    height: 38,
    borderRadius: 9,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
  name: {
    fontWeight: 600,
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  role: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: { height: 22, fontSize: 12, fontWeight: 600, borderRadius: 999 },
  chipModel: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
  meta: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1),
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  metaValue: {
    display: "block",
    marginTop: 2,
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontVariantNumeric: "tabular-nums",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // "1 conexão · 2 setores" quebra linha em vez de cortar.
  metaWrap: { whiteSpace: "normal", overflow: "visible" },
  mono: { fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 12 },
  cardFoot: {
    marginTop: "auto",
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
  },
  smallButton: { textTransform: "none", fontWeight: 600, borderRadius: 8, minWidth: 0 },
  dangerButton: { color: theme.palette.error.main },
  empty: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
  emptyIcon: { fontSize: 48, opacity: 0.35 },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const Prompts = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);

  const [items, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/prompt", { params: { searchParam, pageNumber } });
        dispatch({ type: "LOAD", payload: data.prompts });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });
    socket.on("prompt", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPSERT", payload: data.prompt });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE", payload: +data.promptId });
      }
    });
    return () => socket.disconnect();
  }, [user]);

  const visible = useMemo(() => items.filter((i) => matchesFilter(i, filter)), [items, filter]);
  const countOf = (key) => items.filter((i) => matchesFilter(i, key)).length;

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      const { data } = await api.delete(`/prompt/${deleting.id}`);
      if (data.message === "Prompt deleted") {
        toast.success("Assistente excluído.");
        dispatch({ type: "DELETE", payload: deleting.id });
      } else {
        toast.info(i18n.t(data.message));
      }
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
  };

  const renderCard = (item) => {
    const usage = usageText(item);
    return (
      <div key={item.id} className={classes.card}>
        <div className={classes.cardHead}>
          <span className={classes.badge} aria-hidden="true">
            <EmojiObjectsIcon fontSize="small" />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className={classes.name} title={item.name}>
              {item.name}
            </div>
            <div className={classes.role}>
              Transfere para: {item.queue?.name || "—"}
            </div>
          </div>
        </div>

        <div className={classes.chips}>
          <Chip size="small" label={modelLabel(item.model)} className={`${classes.chip} ${classes.chipModel}`} />
          <Chip
            size="small"
            label={isVoice(item.voice) ? `Voz: ${voiceLabel(item.voice)}` : "Responde em texto"}
            className={`${classes.chip} ${isVoice(item.voice) ? classes.pillGreen : classes.pillGrey}`}
          />
          {item.allowScheduling && (
            <Chip size="small" label="Agenda ativada" className={`${classes.chip} ${classes.pillGreen}`} />
          )}
          {!usage && (
            <Chip size="small" label="Não está em uso" className={`${classes.chip} ${classes.pillAmber}`} />
          )}
        </div>

        <div className={classes.meta}>
          <div style={{ minWidth: 0 }}>
            Em uso em
            <span
              className={`${classes.metaValue} ${classes.metaWrap}`}
              title={[...(item.usedInConnections || []), ...(item.usedInQueues || [])]
                .map((u) => u.name)
                .join(", ")}
            >
              {usage || "—"}
            </span>
          </div>
          <div style={{ minWidth: 0 }}>
            Tokens usados
            <span className={classes.metaValue}>{fmtNumber(item.totalTokens)}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            Chave
            <span className={`${classes.metaValue} ${classes.mono}`}>
              {item.apiKeyHint ? `sk-…${item.apiKeyHint}` : "—"}
            </span>
          </div>
        </div>

        <div className={classes.cardFoot}>
          <Button
            size="small"
            color="primary"
            className={classes.smallButton}
            onClick={() => setModal({ open: true, id: item.id })}
          >
            Editar
          </Button>
          <Button
            size="small"
            className={`${classes.smallButton} ${classes.dangerButton}`}
            onClick={() => setDeleting(item)}
          >
            Excluir
          </Button>
        </div>
      </div>
    );
  };

  const emptyText = searchParam
    ? `Nenhum assistente encontrado para "${searchParam}".`
    : filter !== "all"
    ? "Nenhum assistente neste filtro."
    : "Nenhum assistente cadastrado ainda.";

  const deletingConnections = (deleting?.usedInConnections || []).map((c) => c.name);
  const deletingQueues = (deleting?.usedInQueues || []).map((q) => q.name);

  return (
    <MainContainer>
      <PromptModal
        open={modal.open}
        promptId={modal.id}
        onClose={() => setModal({ open: false, id: null })}
      />
      <ConfirmationModal
        title={deleting ? `Excluir o assistente ${deleting.name}?` : ""}
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        {deletingConnections.length > 0
          ? `Ele está em uso nas conexões: ${deletingConnections.join(", ")}. Tire o assistente dessas conexões antes de excluir.`
          : deletingQueues.length > 0
          ? `Os setores ${deletingQueues.join(", ")} vão ficar sem assistente. Esta ação não pode ser desfeita.`
          : "Esta ação não pode ser desfeita."}
      </ConfirmationModal>

      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>Assistente de IA</Title>
          <Typography variant="body2" className={classes.muted}>
            Atendentes virtuais que respondem pelo WhatsApp usando a OpenAI.
          </Typography>
        </div>
        <div className={classes.headerButtons}>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.headerButton}
            startIcon={<AddIcon />}
            onClick={() => setModal({ open: true, id: null })}
          >
            {isMobile ? "Novo" : "Novo assistente"}
          </Button>
        </div>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar} style={isMobile ? { padding: theme.spacing(1.5) } : undefined}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar assistente"
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
            inputProps={{ "aria-label": "Buscar assistente" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
          <div
            className={`${classes.chipGroup} ${isMobile ? classes.chipGroupScroll : ""}`}
            role="group"
            aria-label="Filtrar assistentes"
          >
            {FILTERS.map(([key, label]) => (
              <Chip
                key={key}
                clickable
                className={classes.filterChip}
                label={`${label} (${countOf(key)})`}
                color={filter === key ? "primary" : "default"}
                variant={filter === key ? "default" : "outlined"}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              />
            ))}
          </div>
        </div>

        <div className={classes.creditNote} role="note">
          <WarningRoundedIcon className={classes.creditIcon} />
          <span>
            <strong>A IA parou de responder?</strong> Geralmente é falta de crédito na OpenAI.{" "}
            <a href="https://platform.openai.com/usage" target="_blank" rel="noopener noreferrer">
              Ver saldo
            </a>
            {" · "}
            <a href="https://platform.openai.com/account/billing/overview" target="_blank" rel="noopener noreferrer">
              Recarregar
            </a>
            {" · "}
            <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
              Chaves
            </a>
          </span>
        </div>

        <div className={classes.grid} onScroll={handleScroll}>
          {visible.map(renderCard)}

          {!loading && visible.length === 0 && (
            <div className={classes.empty}>
              <EmojiObjectsIcon className={classes.emptyIcon} />
              <Typography variant="body2">{emptyText}</Typography>
              {!searchParam && filter === "all" && (
                <>
                  <Typography variant="body2" style={{ maxWidth: 420 }}>
                    Crie um assistente com as instruções do seu negócio e ligue-o a uma conexão
                    ou a um setor para responder os clientes automaticamente.
                  </Typography>
                  <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    className={classes.smallButton}
                    onClick={() => setModal({ open: true, id: null })}
                    style={{ marginTop: 8 }}
                  >
                    Criar primeiro assistente
                  </Button>
                </>
              )}
            </div>
          )}

          {loading && (
            <div className={classes.empty} style={{ padding: theme.spacing(3) }}>
              <CircularProgress size={24} />
            </div>
          )}
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Prompts;
